/**
 * @fileoverview Other resources: the organization's files for the people
 * teaching a program or a class (a safety plan, a floor plan, a classroom
 * layout).
 *
 * Same three-step upload as teaching materials (mint a presigned PUT, upload,
 * confirm), against `/org/programs/{id}/resources` for the program's own and
 * `/org/classes/{id}/resources` for one class's. The file types are the
 * materials allowlist; the size cap is larger, since a scanned plan is often
 * a long PDF and nothing here is emailed. Mirrors backend
 * `services/program_resources.py`, which stays authoritative.
 */

import axios from "axios";
import { formatBytes } from "@/lib/attachments";
import { MATERIAL_ACCEPT, resolveMaterialType } from "@/lib/materials";

/** Mirrors the backend's MAX_RESOURCE_BYTES. */
export const MAX_RESOURCE_BYTES = 25 * 1024 * 1024;
/** Mirrors the backend's MAX_RESOURCES, per program and per class. */
export const MAX_RESOURCES = 25;
/** Mirrors the backend's MAX_TITLE_LENGTH. */
export const MAX_RESOURCE_TITLE = 120;

export const RESOURCE_ACCEPT = MATERIAL_ACCEPT;
export const RESOURCE_HINT = "PDF, Word, PowerPoint, image or audio · up to 25 MB";

/** "Fire safety plan" from "Fire safety plan.pdf", as the backend defaults it. */
export function defaultTitle(filename) {
  const name = filename || "";
  const dot = name.lastIndexOf(".");
  const stem = (dot > 0 ? name.slice(0, dot) : name).trim();
  return (stem || name).slice(0, MAX_RESOURCE_TITLE);
}

/** The reason a file cannot be uploaded, or null. */
export function validateResource(file) {
  if (!resolveMaterialType(file)) return "That file type can't be uploaded. Use a PDF, Word, PowerPoint, image or audio file.";
  if (file.size > MAX_RESOURCE_BYTES) return `Files must be under ${formatBytes(MAX_RESOURCE_BYTES)}.`;
  return null;
}

/** What a tile draws: a thumbnail for a browser-renderable image, else an icon. */
export function resourceKind(contentType) {
  const type = (contentType || "").toLowerCase();
  if (type === "application/pdf") return "pdf";
  if (["image/jpeg", "image/png", "image/webp"].includes(type)) return "image";
  if (type.startsWith("image/")) return "photo";
  if (type.startsWith("audio/")) return "audio";
  if (type.includes("presentationml")) return "slides";
  if (type.includes("wordprocessingml")) return "document";
  return "file";
}

const KIND_LABELS = {
  pdf: "PDF",
  image: "Image",
  photo: "Image",
  audio: "Audio",
  slides: "PowerPoint",
  document: "Word",
  file: "File",
};

/** "PDF · 1.2 MB" under a tile's title. */
export function resourceMeta(resource) {
  const kind = KIND_LABELS[resourceKind(resource?.content_type)];
  return [kind, resource?.size_bytes ? formatBytes(resource.size_bytes) : null].filter(Boolean).join(" · ");
}

/**
 * Mint, upload, confirm. Resolves to the confirmed resource row.
 *
 * @param {object} client the org API client
 * @param {string} basePath `/org/programs/{id}` or `/org/classes/{id}`
 * @param {File} file
 * @param {{title?: string, onProgress?: Function}} options
 */
export async function uploadResource(client, basePath, file, { title, onProgress } = {}) {
  const contentType = resolveMaterialType(file);
  const { data: mint } = await client.post(`${basePath}/resources`, {
    filename: file.name,
    content_type: contentType,
    size_bytes: file.size,
  });
  // Bare axios: the presigned S3 URL must not get the API client's auth
  // headers, and Content-Type must match what the backend signed.
  await axios.put(mint.upload_url, file, {
    headers: { "Content-Type": contentType },
    onUploadProgress: (e) => {
      if (e.total && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    },
  });
  const { data } = await client.post(`${basePath}/resources/${mint.resource_id}/confirm`, {
    filename: file.name,
    content_type: contentType,
    ...(title?.trim() ? { title: title.trim() } : {}),
  });
  return data.resource;
}
